/** Fases y capítulos del camino principiante (multi-camino). */

export type BeginnerPathChapterId = "melody" | "chords";

export type BeginnerPathPhaseId =
  /* Camino 1 — Melodía */
  | "guitar-basics"
  | "scales-learn"
  | "guided-practice"
  | "first-melody"
  /* Camino 2 — Motor rítmico y acordes */
  | "rhythm-basics"
  | "chords-learn"
  | "chord-transitions"
  | "campfire-song";

export interface BeginnerPathPhase {
  id: BeginnerPathPhaseId;
  order: number;
  title: string;
  subtitle: string;
  description: string;
  /** Ruta de producto; vacía o hub si aún es esqueleto. */
  path: string;
  accent: "mint" | "lavender";
  timeEstimateMin: number;
  /** false = visible en mapa, sin módulo de producto aún. */
  available?: boolean;
}

export interface BeginnerPathChapter {
  id: BeginnerPathChapterId;
  order: number;
  title: string;
  subtitle: string;
  description: string;
  /** Fase que debe estar completa para abrir el capítulo (null = siempre abierto). */
  unlockAfterPhaseId: BeginnerPathPhaseId | null;
  phases: BeginnerPathPhase[];
}

/** Camino 1 — Melodía (producto activo). */
export const MELODY_PATH_PHASES: BeginnerPathPhase[] = [
  {
    id: "guitar-basics",
    order: 1,
    title: "Gimnasio motriz",
    subtitle: "Cuerdas, trastes y digitación",
    description:
      "Empieza con el método chileno: identifica las cuerdas al aire y los primeros movimientos en el mástil.",
    path: "/fingers",
    accent: "mint",
    timeEstimateMin: 25,
    available: true,
  },
  {
    id: "scales-learn",
    order: 2,
    title: "Escala y geometría",
    subtitle: "Misiones guiadas con micrófono",
    description:
      "Tres misiones en POS I (3NPS): escucha la caja, luego repítela con el micrófono. Una octava al inicio, extendida e ida y vuelta.",
    path: "/learn/scales",
    accent: "lavender",
    timeEstimateMin: 20,
    available: true,
  },
  {
    id: "guided-practice",
    order: 3,
    title: "Práctica guiada",
    subtitle: "Calentamiento y notas en el mástil",
    description:
      "Una sesión corta que mezcla quiz, clic en el mástil y una subida de escala para afianzar lo aprendido.",
    path: "/practice",
    accent: "mint",
    timeEstimateMin: 10,
    available: true,
  },
  {
    id: "first-melody",
    order: 4,
    title: "Primera canción",
    subtitle: "Estrellita en Do mayor",
    description:
      "Cierra el ciclo con tu primera pieza completa: Estrellita en Do mayor, nota a nota con el mástil y la partitura.",
    path: "/songs/starter-estrellita",
    accent: "lavender",
    timeEstimateMin: 12,
    available: true,
  },
];

/** Camino 2 — Motor rítmico y acordes (esqueleto). */
export const CHORDS_PATH_PHASES: BeginnerPathPhase[] = [
  {
    id: "rhythm-basics",
    order: 1,
    title: "Mano derecha",
    subtitle: "Alternancia i·m y pulgar (p) · pasos progresivos",
    description:
      "Motor de mano derecha por submisiones: caminata i–m, saltos de cuerda, bajos con pulgar y patrón bajo–respuesta p–i–m–i.",
    path: "/learn/rhythm",
    accent: "mint",
    timeEstimateMin: 20,
    available: true,
  },
  {
    id: "chords-learn",
    order: 2,
    title: "Familia Em / Am",
    subtitle: "Geometría Em, E, Am + sonido limpio",
    description:
      "Coloca Em, E y Am como familia geométrica y audita el sonido cuerda a cuerda con arpegio monofónico.",
    path: "/learn/chords",
    accent: "lavender",
    timeEstimateMin: 25,
    available: true,
  },
  {
    id: "chord-transitions",
    order: 3,
    title: "Cambios guiados",
    subtitle: "Traslados de la forma Em ↔ Am ↔ E",
    description:
      "Práctica guiada de cambios y traslados de forma entre Em, Am y E a tempo lento.",
    path: "/learn/path",
    accent: "mint",
    timeEstimateMin: 15,
    available: false,
  },
  {
    id: "campfire-song",
    order: 4,
    title: "Canción fogata",
    subtitle: "Acordes + rasgueo",
    description:
      "Primera canción de fogata aplicando acordes y rasgueo juntos.",
    path: "/learn/path",
    accent: "lavender",
    timeEstimateMin: 15,
    available: false,
  },
];

export const BEGINNER_PATH_CHAPTERS: BeginnerPathChapter[] = [
  {
    id: "melody",
    order: 1,
    title: "Camino 1 · Melodía",
    subtitle: "De cero a tu primera canción",
    description:
      "Gimnasio, escalas, práctica guiada y Estrellita en Do mayor.",
    unlockAfterPhaseId: null,
    phases: MELODY_PATH_PHASES,
  },
  {
    id: "chords",
    order: 2,
    title: "Camino 2 · Acordes",
    subtitle: "Motor rítmico y familia Em/Am",
    description:
      "Mano derecha, primeros acordes, cambios y una canción con rasgueo. Se desbloquea al completar la Primera canción del Camino 1.",
    unlockAfterPhaseId: "first-melody",
    phases: CHORDS_PATH_PHASES,
  },
];

/** Todas las fases (lookup / progreso global). */
export const BEGINNER_PATH_PHASES: BeginnerPathPhase[] =
  BEGINNER_PATH_CHAPTERS.flatMap((c) => c.phases);

/** Alias histórico: fases del Camino 1. */
export const BEGINNER_PATH_MELODY_PHASES = MELODY_PATH_PHASES;

export const BEGINNER_PATH_STORAGE_KEY = "bordona-beginner-path-progress";

export type BeginnerPathProgress = {
  completedPhaseIds: BeginnerPathPhaseId[];
};

export function defaultBeginnerPathProgress(): BeginnerPathProgress {
  return { completedPhaseIds: [] };
}

export function loadBeginnerPathProgress(): BeginnerPathProgress {
  try {
    const raw = localStorage.getItem(BEGINNER_PATH_STORAGE_KEY);
    if (!raw) return defaultBeginnerPathProgress();
    const parsed = JSON.parse(raw) as Partial<BeginnerPathProgress>;
    return {
      completedPhaseIds: Array.isArray(parsed.completedPhaseIds)
        ? (parsed.completedPhaseIds as BeginnerPathPhaseId[])
        : [],
    };
  } catch {
    return defaultBeginnerPathProgress();
  }
}

export function saveBeginnerPathProgress(progress: BeginnerPathProgress): void {
  localStorage.setItem(BEGINNER_PATH_STORAGE_KEY, JSON.stringify(progress));
}

export function markBeginnerPhaseComplete(
  progress: BeginnerPathProgress,
  phaseId: BeginnerPathPhaseId
): BeginnerPathProgress {
  if (progress.completedPhaseIds.includes(phaseId)) return progress;
  return {
    completedPhaseIds: [...progress.completedPhaseIds, phaseId],
  };
}

export function getChapterForPhase(
  phaseId: BeginnerPathPhaseId
): BeginnerPathChapter | null {
  return BEGINNER_PATH_CHAPTERS.find((c) => c.phases.some((p) => p.id === phaseId)) ?? null;
}

/** Cuentas con preview: sin bloqueos de capítulo/fase (desarrollo). */
export const BEGINNER_PATH_PREVIEW_EMAILS = [
  "gonzalompedmusica@gmail.com",
] as const;

export function isBeginnerPathPreviewUser(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return (BEGINNER_PATH_PREVIEW_EMAILS as readonly string[]).includes(normalized);
}

export type BeginnerUnlockOpts = {
  /** Si true, ignora gates de capítulo y cadena secuencial. */
  preview?: boolean;
};

export function isBeginnerChapterUnlocked(
  chapter: BeginnerPathChapter,
  progress: BeginnerPathProgress,
  opts?: BeginnerUnlockOpts
): boolean {
  if (opts?.preview) return true;
  if (!chapter.unlockAfterPhaseId) return true;
  return progress.completedPhaseIds.includes(chapter.unlockAfterPhaseId);
}

/**
 * Capítulo abierto + cadena secuencial dentro del capítulo.
 * Camino 2: bloqueado hasta completar `first-melody` (salvo preview).
 */
export function isBeginnerPhaseUnlocked(
  phaseId: BeginnerPathPhaseId,
  progress: BeginnerPathProgress,
  opts?: BeginnerUnlockOpts
): boolean {
  if (opts?.preview) return true;
  const chapter = getChapterForPhase(phaseId);
  if (!chapter) return false;
  if (!isBeginnerChapterUnlocked(chapter, progress)) return false;

  const idx = chapter.phases.findIndex((p) => p.id === phaseId);
  if (idx <= 0) return true;
  const prev = chapter.phases[idx - 1];
  return progress.completedPhaseIds.includes(prev.id);
}

export function findBeginnerPhase(
  phaseId: BeginnerPathPhaseId
): BeginnerPathPhase | null {
  return BEGINNER_PATH_PHASES.find((p) => p.id === phaseId) ?? null;
}
